import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Link,
  Chip,
  CircularProgress,
  Card,
  CardContent,
  CardMedia,
  Grid,
  Divider,
  Alert,
  IconButton,
  CardHeader,
  Button,
  useTheme,
  useMediaQuery
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AppsIcon from '@mui/icons-material/Apps';
import BuildIcon from '@mui/icons-material/Build';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import SecurityIcon from '@mui/icons-material/Security';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExploreIcon from '@mui/icons-material/Explore';
import SchoolIcon from '@mui/icons-material/School';
import ArticleIcon from '@mui/icons-material/Article';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import InfoIcon from '@mui/icons-material/Info';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ReactMarkdown from 'react-markdown';
import CodeIcon from '@mui/icons-material/Code';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import ExtensionIcon from '@mui/icons-material/Extension';
import StorageIcon from '@mui/icons-material/Storage';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import AppSettingsAltIcon from '@mui/icons-material/AppSettingsAlt'; 
import AppTesterDocumentation from './extensions/AppTesterDocumentation'; 
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import CircleIcon from '@mui/icons-material/Circle';
import AlertTitle from '@mui/material/AlertTitle';
import WorkflowIcon from '@mui/icons-material/AccountTree';
import GettingStartedTab from './extensions/GettingStartedTab';
import WorkflowsTab from './extensions/WorkflowsTab';
import TestScenariosTab from './extensions/TestScenariosTab';
import ToolsTab from './extensions/ToolsTab';
import ActionsTab from './extensions/ActionsTab';
import ApplicationsTab from './extensions/ApplicationsTab';
import TroubleshootingTab from './extensions/TroubleshootingTab';
import BestPracticesTab from './extensions/BestPracticesTab';

interface DocumentationContentProps {
  onBack?: () => void;
}

// TabPanel component for showing content based on selected tab
function TabPanel(props: { children?: React.ReactNode; index: number; value: number }) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`doc-tabpanel-${index}`}
      aria-labelledby={`doc-tab-${index}`}
      {...other}
      style={{ padding: '24px 0' }}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

const DocumentationContent: React.FC<DocumentationContentProps> = ({ onBack }) => {
  const [docContent, setDocContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [tabGroup, setTabGroup] = useState<'basics' | 'advanced'>('basics');
  
  const theme = useTheme();
  const isMediumScreen = useMediaQuery(theme.breakpoints.down('lg'));

  // Define the tab groups with logical organization
  const basicsGroup = [
    { icon: <SchoolIcon />, label: "Getting Started", value: 0 },
    { icon: <AppsIcon />, label: "Applications", value: 1 },
    { icon: <BuildIcon />, label: "Tools", value: 2 },
    { icon: <PlayArrowIcon />, label: "Actions", value: 3 },
    { icon: <AppSettingsAltIcon />, label: "App Tester", value: 4 }
  ];
  
  const advancedGroup = [
  { icon: <PlayCircleOutlineIcon />, label: "Test Scenarios", value: 5 }, 
  { icon: <WorkflowIcon />, label: "Workflows", value: 6 },              
  { icon: <ErrorOutlineIcon />, label: "Troubleshooting", value: 7 },
  { icon: <CheckCircleOutlineIcon />, label: "Best Practices", value: 8 }
  ];

  // Get the active tab within the current group
  const getGroupTabValue = () => {
    const tabs = tabGroup === 'basics' ? basicsGroup : advancedGroup;
    const activeTab = tabs.find(tab => tab.value === tabValue);
    return activeTab ? tabs.indexOf(activeTab) : 0;
  };

  // Handle tab change within a group
  const handleTabChange = (event: React.SyntheticEvent, newGroupIndex: number) => {
    const tabs = tabGroup === 'basics' ? basicsGroup : advancedGroup;
    if (newGroupIndex >= 0 && newGroupIndex < tabs.length) {
      setTabValue(tabs[newGroupIndex].value);
    }
  };

  // Handle group change
  const handleGroupChange = (group: 'basics' | 'advanced') => {
    setTabGroup(group);
    
    // If switching groups, select the first tab in that group
    if (group === 'basics' && !basicsGroup.some(tab => tab.value === tabValue)) {
      setTabValue(basicsGroup[0].value);
    } else if (group === 'advanced' && !advancedGroup.some(tab => tab.value === tabValue)) {
      setTabValue(advancedGroup[0].value);
    }
  };

  useEffect(() => {
    // Load the markdown file (keeping your existing implementation)
    setLoading(true);
    fetch('/docs/getting-started.md', {
      headers: {
        'Accept': 'text/markdown, text/plain, */*'
      }
    })
      .then(response => {
        if (!response.ok) {
          throw new Error(`Failed to load documentation: ${response.status}`);
        }
        if (response.headers.get('content-type')?.includes('text/html')) {
          throw new Error('Received HTML instead of Markdown');
        }
        return response.text();
      })
      .then(text => {
        setDocContent(text);
        setLoading(false);
      })
      .catch(err => {
        console.error('Error loading documentation:', err);
        setError(err.message);
        setLoading(false);
      });
  }, []);

  // Set document title based on active tab
  useEffect(() => {
    const allTabs = [...basicsGroup, ...advancedGroup];
    const activeTab = allTabs.find(tab => tab.value === tabValue);
    if (activeTab) {
      document.title = `AppConfig² - ${activeTab.label}`;
    }
  }, [tabValue]);

  useEffect(() => {
    const handleWorkflowNavigation = (event: CustomEvent) => {
      const { tab, workflow } = event.detail;
      
      // Switch to the specified tab
      setTabValue(tab);
      
      // Set to advanced group if navigating to workflows
      if (tab === 6) {
        setTabGroup('advanced');
      }
      
      // Scroll to specific workflow after tab renders
      setTimeout(() => {
        const element = document.getElementById(`workflow-${workflow}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          
          // Optional: Add a subtle highlight effect
          element.style.backgroundColor = '#e3f2fd';
          setTimeout(() => {
            element.style.backgroundColor = '';
          }, 2000);
        }
      }, 300); // Give time for tab to render
    };

    // Listen for navigation events
    window.addEventListener('navigateToWorkflow', handleWorkflowNavigation as EventListener);
    
    // Cleanup
    return () => {
      window.removeEventListener('navigateToWorkflow', handleWorkflowNavigation as EventListener);
    };
  }, []);  

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const tab = urlParams.get('tab');
    const workflow = urlParams.get('workflow');
    
    if (tab === 'workflows') {
      setTabValue(6); // Workflows tab value
      setTabGroup('advanced');
      
      if (workflow) {
        // Scroll to specific workflow after component renders
        setTimeout(() => {
          const element = document.getElementById(`workflow-${workflow}`);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 500);
      }
    }
  }, []);  

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      {/* Documentation Header */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2, backgroundColor: '#f8f9fc' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="h4" gutterBottom color="primary">
              AppConfig² Documentation
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Complete documentation for managing and testing your Microsoft Entra ID applications.
            </Typography> 
          </Box>
        </Box>

        {/* Enhanced Tab Group Navigation - more distinct and left-aligned */}
        <Box sx={{ 
          mt: 6, 
          mb: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start' // Left aligned
        }}>
          <Box sx={{ 
            display: 'flex',
            bgcolor: 'rgba(0, 0, 0, 0.04)',
            borderRadius: 1.5,
            p: 0.5
          }}>
            <Button
              variant={tabGroup === 'basics' ? 'contained' : 'text'}
              disableElevation
              onClick={() => handleGroupChange('basics')}
              sx={{
                px: 3,
                py: 0.75,
                borderRadius: 1,
                color: tabGroup === 'basics' ? 'white' : 'text.primary',
                fontWeight: tabGroup === 'basics' ? 'medium' : 'normal',
                '&:hover': {
                  bgcolor: tabGroup === 'basics' ? 'primary.main' : 'rgba(0, 0, 0, 0.08)',
                }
              }}
            >
              Basics
            </Button>
            
            <Button
              variant={tabGroup === 'advanced' ? 'contained' : 'text'}
              disableElevation
              onClick={() => handleGroupChange('advanced')}
              sx={{
                px: 3,
                py: 0.75,
                borderRadius: 1,
                color: tabGroup === 'advanced' ? 'white' : 'text.primary',
                fontWeight: tabGroup === 'advanced' ? 'medium' : 'normal',
                '&:hover': {
                  bgcolor: tabGroup === 'advanced' ? 'primary.main' : 'rgba(0, 0, 0, 0.08)',
                }
              }}
            >
              Advanced
            </Button>
          </Box>
        </Box>

        {/* Tabs for the selected group */}
        <Tabs
          value={getGroupTabValue()}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          aria-label={`${tabGroup} documentation tabs`}
          sx={{
            mt: 1.5,
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': { 
              minWidth: isMediumScreen ? 100 : 150,
              fontSize: { xs: '0.75rem', sm: '0.875rem' }
            }
          }}
        >
          {(tabGroup === 'basics' ? basicsGroup : advancedGroup).map((tab, index) => (
            <Tab 
              key={index}
              icon={tab.icon} 
              label={tab.label} 
              iconPosition="start"
              id={`doc-tab-${tab.value}`}
              aria-controls={`doc-tabpanel-${tab.value}`}
            />
          ))}
        </Tabs>
      </Paper>

      {/* Content for each tab */}
      <TabPanel value={tabValue} index={0}>
        <GettingStartedTab setTabValue={setTabValue} />
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <ApplicationsTab />
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <ToolsTab />
      </TabPanel>

      <TabPanel value={tabValue} index={3}>
        <ActionsTab />
      </TabPanel>

      <TabPanel value={tabValue} index={4}>
        <AppTesterDocumentation />
      </TabPanel>

      <TabPanel value={tabValue} index={5}> 
        <TestScenariosTab />
      </TabPanel>

      <TabPanel value={tabValue} index={6}>
        <WorkflowsTab />
      </TabPanel>

      <TabPanel value={tabValue} index={7}>
        <TroubleshootingTab />
      </TabPanel>

      <TabPanel value={tabValue} index={8}>
        <BestPracticesTab />
      </TabPanel>
      
    </Box>
  );
};

export default DocumentationContent;